/**
 * Painel do sino (C-24) — lista agrupada, fiel ao `renderNotifPanel` do V8 (L1282):
 * ponto colorido por severidade, título, descrição, tempo; item não lido com fundo
 * amarelado. Difere do V8 em duas coisas, por decisão do dono (17/10/2026): lista as
 * críticas (em "Precisa da sua atenção") além das secundárias ("Para acompanhar"), e cada
 * item diz o que o clique faz ("Abrir fluxo TARP", "Abrir guia", "Avaliar"…).
 */
import { tempoRelativo, type NotificacaoDoSino } from '@/domain/notificacoes/sino';
import type { NotifSeveridade } from '@/domain/tarp/notificacoes';

/** `_sevColor` do V8. */
const COR_SEV: Record<NotifSeveridade, string> = {
  EMERGENCIA: '#ef4444',
  ALERTA: '#f97316',
  ATENCAO: '#eab308',
  INFO: '#78B4CF',
};

interface ItemProps {
  n: NotificacaoDoSino;
  lida: boolean;
  agora: Date;
  onAbrir: (n: NotificacaoDoSino) => void;
}

function Item({ n, lida, agora, onAbrir }: ItemProps) {
  const cor = COR_SEV[n.sev];
  return (
    <li>
      <button
        type="button"
        onClick={() => onAbrir(n)}
        className={`flex w-full gap-2.5 border-b border-jf-border px-3.5 py-[11px] text-left transition-colors hover:bg-white/[0.03] ${lida ? '' : 'bg-jf-yellow/[0.04]'}`}
      >
        <span aria-hidden className="mt-[5px] h-2 w-2 shrink-0 rounded-full" style={{ background: cor }} />
        <span className="min-w-0 flex-1">
          <span className="block text-[12px] font-bold leading-[1.35] text-white">
            {!lida && <span className="sr-only">Não lida: </span>}
            {n.titulo}
          </span>
          <span className="mt-0.5 block text-[11px] leading-[1.4] text-jf-muted">{n.descricao}</span>
          <span className="mt-1 flex items-center justify-between gap-2">
            <span className="text-[10px] text-jf-dim">{tempoRelativo(n.ts, agora)}</span>
            <span className="text-[10.5px] font-bold" style={{ color: cor }}>
              {n.rotuloAcao} →
            </span>
          </span>
        </span>
      </button>
    </li>
  );
}

interface GrupoProps {
  titulo: string;
  itens: NotificacaoDoSino[];
  lidas: ReadonlySet<string>;
  agora: Date;
  onAbrir: (n: NotificacaoDoSino) => void;
}

function Grupo({ titulo, itens, lidas, agora, onAbrir }: GrupoProps) {
  if (itens.length === 0) return null;
  return (
    <section aria-label={titulo}>
      <h4 className="border-b border-jf-border bg-white/[0.02] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-jf-dim">
        {titulo} ({itens.length})
      </h4>
      <ul>
        {itens.map((n) => (
          <Item key={n.id} n={n} lida={lidas.has(n.chaveLeitura)} agora={agora} onAbrir={onAbrir} />
        ))}
      </ul>
    </section>
  );
}

export interface NotificationPanelProps {
  notificacoes: NotificacaoDoSino[];
  lidas: ReadonlySet<string>;
  isLoading: boolean;
  error: Error | null;
  agora: Date;
  onAbrir: (n: NotificacaoDoSino) => void;
  onMarcarTodas: () => void;
}

export default function NotificationPanel({
  notificacoes, lidas, isLoading, error, agora, onAbrir, onMarcarTodas,
}: NotificationPanelProps) {
  const criticas = notificacoes.filter((n) => n.relev === 'critica');
  const secundarias = notificacoes.filter((n) => n.relev === 'secundaria');
  const temNaoLida = notificacoes.some((n) => !lidas.has(n.chaveLeitura));
  const vazio = notificacoes.length === 0;

  return (
    <>
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-[14px] border-b border-jf-border bg-jf-surface px-3.5 py-3">
        <h3 className="m-0 text-[13px] font-extrabold text-white">Notificações</h3>
        {temNaoLida && (
          <button
            type="button"
            onClick={onMarcarTodas}
            className="text-[11px] text-jf-dim hover:text-white"
          >
            Marcar todas como lidas
          </button>
        )}
      </div>

      {/* Falha de carga é dita, nunca trocada por "nenhuma notificação": num app de segurança
          de barragem, lista vazia por erro seria lida como "nada pendente". */}
      {error && (
        <p role="alert" className="border-b border-jf-border bg-red-500/10 px-3.5 py-2.5 text-[11px] text-red-300">
          Não foi possível carregar todas as notificações. A lista pode estar incompleta —
          confira o Painel e a tela de TARP.
        </p>
      )}

      {vazio && isLoading && !error && (
        <p role="status" className="px-3.5 py-6 text-center text-[12px] text-jf-dim">Carregando…</p>
      )}
      {vazio && !isLoading && !error && (
        <p className="px-3.5 py-[26px] text-center text-[12px] text-jf-dim">
          Nenhuma notificação.
          <br />
          Nada pendente no momento.
        </p>
      )}

      <Grupo titulo="Precisa da sua atenção" itens={criticas} lidas={lidas} agora={agora} onAbrir={onAbrir} />
      <Grupo titulo="Para acompanhar" itens={secundarias} lidas={lidas} agora={agora} onAbrir={onAbrir} />
    </>
  );
}
